import { faGripVertical } from "@fortawesome/free-solid-svg-icons";
import { B2ListGroup } from "@hdb/biome-2";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import Spinner from "react-bootstrap/Spinner";
import InfiniteScroll from "react-infinite-scroll-component";
import * as yup from "yup";
import StatusDisplay from "./dashboard-floorplan-status-display";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { B2Button } from "@hdb/biome-2";
import * as _ from "lodash";
import { createRef, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import ModalComponent from "src/core/components/modal/modal.component";
import HasPermission from "src/core/guards/has-permission";
import FormBuilderComponent, {
  FormConfig,
} from "src/shared/form-builder/form-builder.component";
import { safeAtob } from "src/shared/helpers/enode-decode-string";
import { addEditFloorWithBlockCode } from "src/store/slices/dashboard.slice";
import { rearrangeIndexing } from "src/store/slices/floor.slice";
import "./floorplan.scss";

interface DragDropListProps {
  dataItems: any[];
  setDataItems: React.Dispatch<React.SetStateAction<any[]>>;
  hasMore: boolean;
  fetchMoreData: () => void;
  navigateToUrl: (
    url: string,
    isReplaced?: boolean,
    isClicked?: boolean
  ) => void;
  resetComponent: any;
}

const DragDropList: React.FC<DragDropListProps> = ({
  dataItems,
  setDataItems,
  hasMore,
  fetchMoreData,
  navigateToUrl,
  resetComponent,
}) => {
  const params: any = useParams();
  /*   const location: any = useLocation();
  const { pathname } = location;
  const navigate = useNavigate(); */
  const blockCode: any = params.blockCode ? safeAtob(params.blockCode) : null;

  const floor = params?.floor;
  const formRef = createRef();
  const dispatch = useDispatch();
  const [addLevelModal, setAddLevelModal] = useState(false);

  const onSubmitLevelModal = () => {
    const form: any = formRef.current;
    if (form) {
      form.handleSubmit();
      const { blockCode, FloorCode } = form.values;

      if (!blockCode || !FloorCode || FloorCode?.length > 100) {
        return;
      }

      dispatch(addEditFloorWithBlockCode({ blockCode, Id: 0, FloorCode })).then(
        (res: any) => {
          setAddLevelModal(false);

          if (res.payload.status === 201) {
            toast.success(res.payload.message, {
              position: "top-right",
              autoClose: 3000,
              hideProgressBar: false,
              closeOnClick: false,
              pauseOnFocusLoss: true,
              draggable: true,
              pauseOnHover: true,
            });
          }

          resetComponent();
        }
      );
    }
  };

  const addLevelModalActionConfig: any = [
    {
      label: "Save",
      event: onSubmitLevelModal,
      variant: "primary",
    },
  ];

  const handleOnDragEnd = (result: any) => {
    const { destination, source } = result;

    if (!destination) {
      return;
    }

    if (destination.index === source.index) {
      return;
    }

    const data = _.cloneDeep(dataItems);

    const [element] = data.splice(source.index, 1);

    // Insert the removed element at index 1
    data.splice(destination.index, 0, element);

    const rearrangedData = data.map((item: any, index: any) => ({
      id: item.Id,
      index: index,
    }));

    dispatch(rearrangeIndexing({ payload: rearrangedData })).then(() => {
      resetComponent();
    });
  };

  const [initialValues, setInitialValues] = useState<any>({
    blockCode: blockCode,
    FloorCode: "",
  });

  const validationSchema = yup.object().shape({
    FloorCode: yup
      .string()
      .required("FloorCode is required")
      .max(100, "Floor Code must be at most 100 characters"),
  });

  const formConfig: FormConfig = {
    controls: [
      {
        name: "blockCode",
        label: "Block Code",
        placeholder: "",
        type: "text",
        required: true,
        readOnly: true,
        disabled: true,
      },
      {
        name: "FloorCode",
        label: "Floor",
        placeholder: "",
        type: "text",
        required: true,
      },
    ],
  };

  // Responsive height state
  const [scrollHeight, setScrollHeight] = useState(
    window.innerWidth > 1200 ? 1200 : 250
  );

  useEffect(() => {
    const handleResize = () => {
      setScrollHeight(window.innerWidth > 1200 ? 1200 : 250);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      <div className="w-lg-25 z-1 position-relative ">
        <DragDropContext onDragEnd={handleOnDragEnd}>
          <Droppable droppableId="droppable" direction="vertical">
            {(provided: any) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                <B2ListGroup>
                  <div className="mb-2">
                    <HasPermission
                      moduleName="Floor Plan Detail"
                      actionName="Update-Floor Plan Detail"
                      isRoute={false}
                    >
                      <B2Button
                        variant="primary"
                        className="cursor-pointer w-100"
                        onClick={() => setAddLevelModal?.(true)} // Optional chaining for function
                      >
                        Add Level
                      </B2Button>
                    </HasPermission>
                  </div>
                  <div
                    id="floor-scrollable-div"
                    style={{
                      maxHeight: scrollHeight,
                      overflowY: "auto",
                    }}
                  >
                    <InfiniteScroll
                      scrollableTarget="floor-scrollable-div"
                      dataLength={dataItems?.length ?? 0}
                      next={fetchMoreData}
                      hasMore={hasMore}
                      loader={
                        <div className="text-center p-2">
                          <Spinner
                            animation="border"
                            style={{ color: "#1976D2" }}
                            className="mt-5"
                          />
                        </div>
                      }
                      style={{ overflowX: "hidden" }}
                      endMessage={
                        <p style={{ textAlign: "center", paddingTop: "10px" }}>
                          <b style={{ color: "#1976D2" }}>No More Data</b>
                        </p>
                      }
                    >
                      {dataItems?.map((item: any, i: any) => (
                        <Draggable
                          key={item?.FloorCode} // Optional chaining for FloorCode
                          draggableId={item?.FloorCode ?? `${i}`} // Ensure a fallback in case FloorCode is undefined
                          index={i}
                        >
                          {(provided: any) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                            >
                              <B2ListGroup.Item
                                active={false}
                                key={i}
                                onClick={() =>
                                  navigateToUrl(
                                    `${encodeURIComponent(
                                      item?.FloorCode,
                                    )}?update=true`,
                                    false,
                                    true,
                                  )
                                }
                                className={`hide-expand-icon cursor-pointer ${floor === item?.FloorCode
                                  ? "active active-without-child"
                                  : ""
                                  }`}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    width: "100%",
                                  }}
                                >
                                  <FontAwesomeIcon
                                    icon={faGripVertical}
                                    className="fs-7 me-2"
                                    style={{
                                      width: "20px",
                                      height: "20px",
                                      cursor: "grab",
                                    }}
                                  />
                                  <div
                                    style={{
                                      color: "black",
                                      whiteSpace: "normal",
                                      overflowWrap: "anywhere",
                                      wordBreak: "break-all",
                                    }}
                                  >
                                    {item?.FloorCode}
                                  </div>
                                  <StatusDisplay
                                    item={item}
                                    id={i}
                                    setState={setDataItems}
                                    navigateToUrl={navigateToUrl}
                                    resetComponent={resetComponent}
                                  />
                                </div>
                              </B2ListGroup.Item>
                            </div>
                          )}
                        </Draggable>
                      ))}
                    </InfiniteScroll>
                  </div>
                </B2ListGroup>
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      {addLevelModal && (
        <ModalComponent
          headerTitle="Add Floor Level"
          showModal={addLevelModal}
          handleClose={() => setAddLevelModal(false)}
          actionConfig={addLevelModalActionConfig}
        >
          <FormBuilderComponent
            formRef={formRef}
            initialValues={initialValues}
            validation={validationSchema}
            formConfig={formConfig}
          />
        </ModalComponent>
      )}
    </>
  );
};

export default DragDropList;
